/**
 * Unit Preferences Settings Page
 * Allows users to customize their preferred units for different measurements
 * Until the user chooses, the units follow the app language (English: US
 * units, other languages: metric); "reset" returns to that.
 */

import { Check, Globe, RefreshCw, Settings } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";
import {
  UnitType,
  useUnitPreferencesStore,
} from "../../stores/unitPreferencesStore";

const UnitPreferencesSettings: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useI18nStore();
  const {
    getEffectivePreferences,
    useNationalityDefaults,
    loading,
    loadPreferences,
    updatePreference,
    resetToNationalityDefaults,
    getUnitDisplay,
  } = useUnitPreferencesStore();

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user, loadPreferences]);

  const handleUnitChange = async (unitType: UnitType, unit: string) => {
    setSaving(true);
    setSaveSuccess(false);

    try {
      await updatePreference(unitType, unit);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      // Toast notification with unit display
      const unitDisplay = getUnitDisplay(unit);
      toast.success(
        t.unitsChangedToast
          .replace("{type}", unitTitles[unitType])
          .replace("{unit}", unitDisplay),
        {
          icon: "✅",
          duration: 3000,
        },
      );
    } catch (error: any) {
      console.error("Failed to update preference:", error);
      toast.error(t.unitsUpdateFailed, {
        icon: "❌",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!user) return;

    setSaving(true);
    try {
      await resetToNationalityDefaults();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      toast.success(t.unitsResetToast, {
        icon: "🔄",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Failed to reset preferences:", error);
      toast.error(t.unitsResetFailed, {
        icon: "❌",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const unitTitles: Record<UnitType, string> = {
    weight: t.unitsWeightTitle,
    height: t.unitsHeightTitle,
    temperature: t.unitsTemperatureTitle,
    bloodGlucose: t.unitsBloodGlucoseTitle,
    cholesterol: t.unitsCholesterolTitle,
  };

  // `value` is the stored UCUM code; only the labels are translated.
  const unitOptions = {
    weight: [
      { value: "kg", label: t.unitsKilograms, common: t.unitsMostCountries },
      { value: "[lb_av]", label: t.unitsPounds, common: t.unitsRegionUs },
      { value: "[stone_av]", label: t.unitsStones, common: t.unitsRegionUk },
    ],
    height: [
      { value: "cm", label: t.unitsCentimeters, common: t.unitsMostCountries },
      { value: "[in_i]", label: t.unitsInches, common: t.unitsRegionUs },
      { value: "[ft_i]", label: t.unitsFeetInches, common: t.unitsRegionUsUk },
    ],
    temperature: [
      { value: "Cel", label: t.unitsCelsius, common: t.unitsMostCountries },
      { value: "[degF]", label: t.unitsFahrenheit, common: t.unitsRegionUs },
    ],
    bloodGlucose: [
      { value: "mmol/L", label: "mmol/L", common: t.unitsMostCountries },
      { value: "mg/dL", label: "mg/dL", common: t.unitsRegionUsJapan },
    ],
    cholesterol: [
      { value: "mmol/L", label: "mmol/L", common: t.unitsMostCountries },
      { value: "mg/dL", label: "mg/dL", common: t.unitsRegionUs },
    ],
  };

  const renderUnitSelector = (
    unitType: UnitType,
    title: string,
    description: string,
  ) => {
    const currentValue = getEffectivePreferences()[unitType];
    const options = unitOptions[unitType];

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>

        <div className="space-y-3">
          {options.map((option) => (
            <label
              key={option.value}
              htmlFor={`${unitType}-${option.value}`}
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                currentValue === option.value
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              aria-label={`${option.label} - ${option.common}`}
            >
              <input
                id={`${unitType}-${option.value}`}
                type="radio"
                name={unitType}
                value={option.value}
                checked={currentValue === option.value}
                onChange={(e) => handleUnitChange(unitType, e.target.value)}
                disabled={saving}
                className="w-4 h-4 text-blue-600"
                aria-label={t.unitsSelectOption.replace("{unit}", option.label)}
              />
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">
                    {option.label}
                  </span>
                  {currentValue === option.value && (
                    <Check className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                <span className="text-sm text-gray-500">{option.common}</span>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">{t.unitsLoginRequired}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Settings className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">
            {t.unitsPageTitle}
          </h1>
        </div>
        <p className="text-gray-600">{t.unitsPageDescription}</p>
      </div>

      {/* App-language default info */}
      {useNationalityDefaults && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">
              {t.unitsFollowLanguageTitle}
            </p>
            <p className="text-sm text-blue-700 mt-1">
              {t.unitsFollowLanguageDescription}
            </p>
          </div>
        </div>
      )}

      {/* Save Success Notification */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-sm font-medium text-green-900">{t.unitsSaved}</p>
        </div>
      )}

      {/* Unit Selectors */}
      <div className="space-y-6">
        {renderUnitSelector(
          "weight",
          unitTitles.weight,
          t.unitsWeightDescription,
        )}

        {renderUnitSelector(
          "height",
          unitTitles.height,
          t.unitsHeightDescription,
        )}

        {renderUnitSelector(
          "temperature",
          unitTitles.temperature,
          t.unitsTemperatureDescription,
        )}

        {renderUnitSelector(
          "bloodGlucose",
          unitTitles.bloodGlucose,
          t.unitsBloodGlucoseDescription,
        )}

        {renderUnitSelector(
          "cholesterol",
          unitTitles.cholesterol,
          t.unitsCholesterolDescription,
        )}
      </div>

      {/* Reset Button */}
      <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t.unitsResetTitle}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{t.unitsResetDescription}</p>
        <button
          onClick={handleReset}
          disabled={saving || useNationalityDefaults}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${saving ? "animate-spin" : ""}`} />
          {t.unitsResetButton}
        </button>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
            <span className="text-gray-900">{t.unitsLoading}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitPreferencesSettings;
